import type { ReactNode, RefObject } from "react";

import { ArrowClockwiseIcon, XIcon } from "@phosphor-icons/react";
import {
  Box,
  Flex,
  IconButton,
  SegmentedControl,
  Text,
} from "@radix-ui/themes";

import type {
  PortfolioComparisonChartSeries,
  PortfolioHistoryChartPoint,
} from "./portfolioHistory";
import {
  PORTFOLIO_LOOKBACKS,
  type PortfolioLookback,
} from "./portfolioHistoryTimeSettings";

interface PortfolioHistoryChartPresentationProps {
  chartContainerRef: RefObject<HTMLDivElement>;
  comparisons?: PortfolioComparisonChartSeries[];
  comparisonSearch?: ReactNode;
  data: PortfolioHistoryChartPoint[];
  hasError: boolean;
  isLoading: boolean;
  isPortfolioFetching?: boolean;
  lookback: PortfolioLookback;
  onRemoveComparison?: (symbol: string) => void;
  onLookbackChange: (lookback: PortfolioLookback) => void;
  onRefresh?: () => void;
  shouldRenderChart: boolean;
}

const fillClassName = "h-full min-h-0 w-full";

const ChartStatusMessage = ({
  color,
  message,
}: {
  color: "gray" | "red";
  message: string;
}) => (
  <Flex className={fillClassName} align="center" justify="center">
    <Text size="2" color={color}>
      {message}
    </Text>
  </Flex>
);

const getChartContent = ({
  chartContainerRef,
  data,
  hasError,
  isLoading,
  shouldRenderChart,
}: Pick<
  PortfolioHistoryChartPresentationProps,
  "chartContainerRef" | "data" | "hasError" | "isLoading" | "shouldRenderChart"
>): ReactNode => {
  if (isLoading) {
    return (
      <ChartStatusMessage color="gray" message="Loading portfolio history" />
    );
  }

  if (hasError) {
    return (
      <ChartStatusMessage
        color="red"
        message="Portfolio history is unavailable."
      />
    );
  }

  if (data.length === 0) {
    return (
      <ChartStatusMessage
        color="gray"
        message="No portfolio history is available for this period."
      />
    );
  }

  if (shouldRenderChart) {
    return <div ref={chartContainerRef} className={fillClassName} />;
  }

  return null;
};

export const PortfolioHistoryChartPresentation = ({
  chartContainerRef,
  comparisons = [],
  comparisonSearch,
  data,
  isLoading,
  isPortfolioFetching = false,
  hasError,
  lookback,
  onRemoveComparison,
  onLookbackChange,
  onRefresh,
  shouldRenderChart,
}: PortfolioHistoryChartPresentationProps) => {
  return (
    <Flex direction="column" className={fillClassName}>
      <Flex align="center" gap="2" justify="between" px="2" pt="2" wrap="wrap">
        <Flex align="center" gap="2">
          {comparisonSearch ? (
            <Box width="180px">{comparisonSearch}</Box>
          ) : null}
          {comparisons.length > 0 ? (
            <Text color="yellow" size="1" weight="medium">
              Portfolio
            </Text>
          ) : null}
          {comparisons.map(({ color, hasError, isLoading, symbol }) => (
            <Flex align="center" gap="1" key={symbol}>
              <Text color={color} size="1" weight="medium">
                {symbol}
              </Text>
              {isLoading ? (
                <Text color="gray" size="1">
                  Loading
                </Text>
              ) : null}
              {hasError ? (
                <Text color="red" size="1">
                  Unavailable
                </Text>
              ) : null}
              {onRemoveComparison ? (
                <IconButton
                  aria-label={`Remove ${symbol} comparison`}
                  color="gray"
                  size="1"
                  type="button"
                  variant="ghost"
                  onClick={() => onRemoveComparison(symbol)}
                >
                  <XIcon aria-hidden size={12} />
                </IconButton>
              ) : null}
            </Flex>
          ))}
        </Flex>
        <SegmentedControl.Root
          aria-label="Portfolio history lookback"
          size="1"
          value={lookback}
          onValueChange={(value) => {
            onLookbackChange(value as PortfolioLookback);
          }}
        >
          {PORTFOLIO_LOOKBACKS.map((option) => (
            <SegmentedControl.Item key={option.value} value={option.value}>
              {option.label}
            </SegmentedControl.Item>
          ))}
        </SegmentedControl.Root>
        {onRefresh ? (
          <IconButton
            aria-label="Refresh portfolio history"
            color="gray"
            disabled={isPortfolioFetching}
            size="1"
            type="button"
            variant="ghost"
            onClick={onRefresh}
          >
            <ArrowClockwiseIcon aria-hidden size={14} />
          </IconButton>
        ) : null}
      </Flex>
      <Flex flexGrow="1" minHeight="0">
        {getChartContent({
          chartContainerRef,
          data,
          hasError,
          isLoading,
          shouldRenderChart,
        })}
      </Flex>
    </Flex>
  );
};
